import {
  ImportType,
  InvoiceBalances,
  MatterOpeningBalances,
  MigrationSetImport,
  TemplateKey,
  UnbilledExpenseBalances,
  UnbilledTimeFeeBalances,
} from "../shared";
import { formatCurrency } from "../shared/utils";

export function getDefaultCardState(
  isReady: boolean = true,
  isDisabled: boolean = false,
) {
  return {
    hasUploaded: false,
    hasWarnings: false,
    hasErrors: false,
    uploadWarningCount: 0,
    uploadErrorCount: 0,
    uploadTotalRecordCount: 0,
    isSuccess: false,
    isReady: isReady,
    isDisabled: isDisabled,
    needsRevalidation: false,
  };
}

export function getTemplateKey(
  importType: ImportType,
  region: string,
): TemplateKey {
  switch (region) {
    case "US":
      switch (importType) {
        case "contact":
          return "contactImportUS";
        case "matter":
          return "matterImportUS";
        case "matterType":
          return "matterTypeImportUS";
        case "matterOpeningBalance":
          return "matterOpeningBalanceImportUS";
        case "unbilledTimeAndFee":
          return "unbilledTimeAndFeeImportUS";
        case "unbilledExpense":
          return "unbilledExpenseImportUS";
        case "invoice":
          return "invoiceImportUS";
        case "invoiceDetailed":
          return "invoiceDetailedImportUS";
        case "invoiceItem":
          return "invoiceItemImportUS";
        default:
          throw new Error(
            `Unsupported import type: ${importType} for region: ${region}`,
          );
      }
    case "AU":
      switch (importType) {
        case "contact":
          return "contactImportAU";
        case "matter":
          return "matterImportAU";
        case "matterType":
          return "matterTypeImportAU";
        case "matterOpeningBalance":
          return "matterOpeningBalanceImportAU";
        case "unbilledTimeAndFee":
          return "unbilledTimeAndFeeImportAU";
        case "unbilledExpense":
          return "unbilledExpenseImportAU";
        case "invoice":
          return "invoiceImportAU";
        case "invoiceDetailed":
          return "invoiceDetailedImportAU";
        case "invoiceItem":
          return "invoiceItemImportAU";
        default:
          throw new Error(
            `Unsupported import type: ${importType} for region: ${region}`,
          );
      }
    case "GB":
      switch (importType) {
        case "contact":
          return "contactImportGB";
        case "matter":
          return "matterImportGB";
        case "matterType":
          return "matterTypeImportGB";
        case "matterOpeningBalance":
          return "matterOpeningBalanceImportGB";
        case "unbilledTimeAndFee":
          return "unbilledTimeAndFeeImportGB";
        case "unbilledExpense":
          return "unbilledExpenseImportGB";
        case "invoice":
          return "invoiceImportGB";
        case "invoiceDetailed":
          return "invoiceDetailedImportGB";
        case "invoiceItem":
          return "invoiceItemImportGB";
        default:
          throw new Error(
            `Unsupported import type: ${importType} for region: ${region}`,
          );
      }
    default:
      throw new Error(`Unsupported region: ${region}`);
  }
}

export function getMatterOpeningBalancesDescription(
  region: string,
  migrationSetImport: MigrationSetImport | undefined,
): {
  heading?: string;
  details?: { label: string; value: string }[];
  simpleText?: string;
} {
  if (!migrationSetImport) {
    switch (region) {
      case "AU":
        return {
          simpleText: "Import Matter Credit and Trust account balances.",
        };
      case "GB":
        return {
          simpleText: "Import Matter Credit and Client account Balances.",
        };
      case "US":
        return {
          simpleText:
            "Import Matter Operating Retainer, Credit, and Trust account Balances.",
        };
      default:
        return {
          simpleText:
            "Import Matter Operating Retainer, Credit, and Trust account Balances.",
        };
    }
  }

  if (migrationSetImport.hasUploaded) {
    const balances = migrationSetImport.balances as MatterOpeningBalances;
    const heading = "Total account balances:";
    const regionDetails = {
      AU: [
        { label: "Credit:", value: formatCurrency(balances?.credit, "AU") },
        { label: "Trust:", value: formatCurrency(balances?.trust, "AU") },
      ],
      GB: [
        { label: "Credit:", value: formatCurrency(balances?.credit, "GB") },
        { label: "Client:", value: formatCurrency(balances?.trust, "GB") },
      ],
      US: [
        { label: "Credit:", value: formatCurrency(balances?.credit, "US") },
        { label: "Trust:", value: formatCurrency(balances?.trust, "US") },
        {
          label: "Operating:",
          value: formatCurrency(balances?.operating, "US"),
        },
      ],
    };

    const details = regionDetails[region as keyof typeof regionDetails] || [];
    return { heading, details };
  } else {
    return {
      simpleText:
        "Import Matter Operating Retainer, Credit, and Trust account Balances.",
    };
  }
}

export function getActivitiesDescription(region: string): {
  heading?: string;
  simpleText?: string;
} {
  switch (region) {
    case "AU":
      return {
        simpleText:
          "Imported Activity Types can be used for time, fee, or disbursement entries that are created once you are up and running with Smokeball. Activity Types are not required in order to import Unbilled Entries.",
      };
    case "GB":
      return {
        simpleText:
          "Imported Activity Types can be used for time, fee, or cost recovery entries that are created once you are up and running with Smokeball. Activity Types are not required in order to import Unbilled Entries.",
      };
    case "US":
      return {
        simpleText:
          "Imported Activity Types can be used for time, fee, or expense entries that are created once you are up and running with Smokeball. Activity Types are not required in order to import Unbilled Entries.",
      };
    default:
      return {
        simpleText:
          "Imported Activity Types can be used for time, fee, or expense entries that are created once you are up and running with Smokeball. Activity Types are not required in order to import Unbilled Entries.",
      };
  }
}

export function getUnbilledTimeAndFeesDescription(
  region: string,
  migrationSetImport: MigrationSetImport | undefined,
): {
  heading?: string;
  details?: { label: string; value: string }[];
  simpleText?: string;
} {
  if (!migrationSetImport) {
    switch (region) {
      case "AU":
        return {
          simpleText:
            "Import Unbilled Time/Fee Entries. The default GST rate should be configured in the settings of your Smokeball account before uploading Unbilled entries. Unbilled Entries must be associated with a Matter.",
        };
      case "GB":
        return {
          simpleText:
            "Import Unbilled Time/Fee Entries. The default VAT rate should be configured in the settings of your Smokeball account before uploading Unbilled entries. Unbilled Entries must be associated with a Matter.",
        };
      case "US":
        return {
          simpleText:
            "Import Unbilled Time/Fee Entries. Unbilled Entries must be associated with a Matter.",
        };
      default:
        return {
          simpleText:
            "Import Unbilled Time/Fee Entries. Unbilled Entries must be associated with a Matter.",
        };
    }
  }

  if (migrationSetImport.hasUploaded) {
    const balances = migrationSetImport.balances as UnbilledTimeFeeBalances;
    const heading = "Total amounts:";
    const regionDetails = {
      AU: [
        { label: "Exc. GST:", value: formatCurrency(balances?.totalex, "AU") },
        { label: "Inc. GST:", value: formatCurrency(balances?.totalinc, "AU") },
        { label: "", value: "" },
        { label: "(inc. non-billable and written off entries)", value: "" },
      ],
      GB: [
        { label: "Exc. VAT:", value: formatCurrency(balances?.totalex, "GB") },
        { label: "Inc. VAT:", value: formatCurrency(balances?.totalinc, "GB") },
        { label: "", value: "" },
        { label: "(inc. non-billable and written off entries)", value: "" },
      ],
      US: [
        { label: "", value: formatCurrency(balances?.totalex, "US") },
        { label: "", value: "" },
        { label: "(inc. non-billable and written off entries)", value: "" },
      ],
    };
    const details = regionDetails[region as keyof typeof regionDetails] || [];
    return { heading, details };
  } else {
    return {
      simpleText:
        "Import Unbilled Time/Fee Entries. Unbilled Entries must be associated with a Matter.",
    };
  }
}

export function getUnbilledExpensesDescription(
  region: string,
  migrationSetImport: MigrationSetImport | undefined,
): {
  heading?: string;
  details?: { label: string; value: string }[];
  simpleText?: string;
} {
  if (!migrationSetImport) {
    switch (region) {
      case "AU":
        return {
          simpleText:
            "Import Unbilled Disbursement Entries. The default GST rate should be configured in the settings of your Smokeball account before uploading Unbilled Entries. Unbilled entries must be associated with a Matter.",
        };
      case "GB":
        return {
          simpleText:
            "Import Unbilled Cost Recovery Entries. The default VAT rate should be configured in the settings of your Smokeball account before uploading Unbilled Untries. Unbilled Entries must be associated with a Matter.",
        };
      case "US":
        return {
          simpleText:
            "Import Unbilled Expense Entries. Unbilled Entries must be associated with a Matter.",
        };
      default:
        return {
          simpleText:
            "Import Unbilled Expense Entries. Unbilled Entries must be associated with a Matter.",
        };
    }
  }

  if (migrationSetImport.hasUploaded) {
    const balances = migrationSetImport.balances as UnbilledExpenseBalances;
    const heading = "Total amounts:";
    const regionDetails = {
      AU: [
        { label: "Exc. GST:", value: formatCurrency(balances?.totalex, "AU") },
        { label: "Inc. GST:", value: formatCurrency(balances?.totalinc, "AU") },
        { label: "", value: "" },
        { label: "(inc. non-billable and written off entries)", value: "" },
      ],
      GB: [
        { label: "Exc. VAT:", value: formatCurrency(balances?.totalex, "GB") },
        { label: "Inc. VAT:", value: formatCurrency(balances?.totalinc, "GB") },
        { label: "", value: "" },
        { label: "(inc. non-billable and written off entries)", value: "" },
      ],
      US: [
        { label: "", value: formatCurrency(balances?.totalex, "US") },
        { label: "", value: "" },
        { label: "(inc. non-billable and written off entries)", value: "" },
      ],
    };
    const details = regionDetails[region as keyof typeof regionDetails] || [];
    return { heading, details };
  } else {
    return {
      simpleText:
        "Import unbilled expense entries. Matters must be uploaded and valid before Unbilled Entries can be added.",
    };
  }
}

export function getInvoicesDescription(
  region: string,
  migrationSetImport: MigrationSetImport | undefined,
  invoiceType: string,
): {
  heading?: string;
  details?: { label: string; value: string }[];
  simpleText?: string;
} {
  // Before upload of data
  if (!migrationSetImport) {
    const basicTextByRegion: Record<string, string> = {
      AU: "Upload all required Invoices for import. One summary Fee and/or Disbursement line item will be created per Invoice based on the totals provided. Ensure the default GST rate is set in Smokeball Firm Settings before upload.",
      GB: "Upload all required Invoices for import. One summary Fee and/or Cost Recovery line item will be created per Invoice based on the totals provided. Ensure the default VAT rate is set in Smokeball Firm Settings before upload.",
      US: "Upload all required Invoices for import. One summary Fee and/or Expense line item will be created per Invoice based on the totals provided.",
    };

    const detailedTextByRegion: Record<string, string> = {
      AU: "Upload all required Invoices for import. Must be accompanied by an Invoice Items upload file detailing one or more line items per Invoice. Ensure the default GST rate is set in Smokeball Firm Settings before upload.",
      GB: "Upload all required Invoices for import. Must be accompanied by an Invoice Items upload file detailing one or more line items per Invoice. Ensure the default VAT rate is set in Smokeball Firm Settings before upload.",
      US: "Upload all required Invoices for import. Must be accompanied by an Invoice Items upload file detailing one or more line items per Invoice.",
    };

    if (invoiceType === "basicInvoiceType") {
      return {
        simpleText: basicTextByRegion[region] || basicTextByRegion["US"],
      };
    }

    if (invoiceType === "detailedInvoiceType") {
      return {
        simpleText: detailedTextByRegion[region] || detailedTextByRegion["US"],
      };
    }

    // noInvoiceType fallback to USA basic wording (not seen in UI)
    return {
      simpleText:
        "Upload all required Invoices for import. One summary Fee and/or Expense line item will be created per Invoice based on the totals provided.",
    };
  }

  // After upload of data
  if (migrationSetImport.hasUploaded) {
    const balances = migrationSetImport.balances as InvoiceBalances;

    const commonFinancialDetailsByRegion: Record<
      string,
      { label: string; value: string }[]
    > = {
      AU: [
        {
          label: "Total (Inc. GST):",
          value: formatCurrency(balances?.totalinc, "AU"),
        },
        {
          label: "Amount Due (Inc. GST):",
          value: formatCurrency(balances?.dueinc, "AU"),
        },
      ],
      GB: [
        {
          label: "Total (Inc. VAT):",
          value: formatCurrency(balances?.totalinc, "GB"),
        },
        {
          label: "Amount Due (Inc. VAT):",
          value: formatCurrency(balances?.dueinc, "GB"),
        },
      ],
      US: [
        {
          label: "Total:",
          value: formatCurrency(balances?.totalinc, "US"),
        },
        {
          label: "Amount Due:",
          value: formatCurrency(balances?.dueinc, "US"),
        },
      ],
    };

    const basicDetailsByRegion: Record<
      string,
      { label: string; value: string }[]
    > = {
      AU: [
        ...commonFinancialDetailsByRegion.AU,
        {
          label: "Fee Total (Exc./Inc. GST):",
          value: `${formatCurrency(balances?.feeex, "AU")}/${formatCurrency(
            balances?.feeinc,
            "AU",
          )}`,
        },
        {
          label: "Disb Total (Exc./Inc. GST):",
          value: `${formatCurrency(balances?.expenseex, "AU")}/${formatCurrency(
            balances?.expenseinc,
            "AU",
          )}`,
        },
      ],
      GB: [
        ...commonFinancialDetailsByRegion.GB,
        {
          label: "Fee Total (Exc./Inc. VAT):",
          value: `${formatCurrency(balances?.feeex, "GB")}/${formatCurrency(
            balances?.feeinc,
            "GB",
          )}`,
        },
        {
          label: "Cost Rec. Total (Exc./Inc. VAT):",
          value: `${formatCurrency(balances?.expenseex, "GB")}/${formatCurrency(
            balances?.expenseinc,
            "GB",
          )}`,
        },
      ],
      US: [
        ...commonFinancialDetailsByRegion.US,
        {
          label: "Fee Total:",
          value: formatCurrency(balances?.feeinc, "US"),
        },
        {
          label: "Expense Total:",
          value: formatCurrency(balances?.expenseinc, "US"),
        },
      ],
    };

    if (invoiceType === "basicInvoiceType") {
      return {
        details: basicDetailsByRegion[region] || basicDetailsByRegion["US"],
      };
    }

    if (invoiceType === "detailedInvoiceType") {
      return {
        details:
          commonFinancialDetailsByRegion[region] ||
          commonFinancialDetailsByRegion["US"],
      };
    }    
  }

  return {
    simpleText:
      "Upload all required Invoices for import. One summary Fee and/or Expense line item will be created per Invoice based on the totals provided.",
  };
}
